import * as React from 'react';
import { Popover } from "antd";
import { format, parseISO, isValid } from 'date-fns';
import { DayPicker, type Matcher, type Modifiers } from 'react-day-picker';
import DateHelper from 'app/helpers/dateHelper';

const PrettyDateFormat = 'dd MMM yyyy';

const monthNames : string[] = [ ];
(function() {
    for(let i = 0; i < 12; ++i) {
        monthNames.push(format(new Date(2000, i, 1), 'MMM'));
    }
})();

function parseMonthName(name:string) {
    for (let i = 0; i < monthNames.length; ++i) {
        if (monthNames[i] == name) {
            return i;
        }
    }

    return undefined;
}

function parsePretty(dateStr:string) {
    const ddmmyyyyMatcher = /^\s*([0-9]{2})([0-9]{2})([0-9]{4})\s*$/;
    const ddmmyyyyResult = ddmmyyyyMatcher.exec(dateStr);
    if (ddmmyyyyResult) {
        const day = parseInt(ddmmyyyyResult[1], 10);
        const month = parseInt(ddmmyyyyResult[2], 10) - 1;
        const year = parseInt(ddmmyyyyResult[3], 10);
        const date = new Date(year, month, day);
        if (date.getDate() === day && month === date.getMonth() && year === date.getFullYear())
            return date;
        return new Date('Invalid');
    }

    const matcher = /^\s*([0-9]{1,2})[\- \/\\]+([A-Za-z]{3}|[0-9]{1,2})[\- \/\\]+([0-9]{4})\s*$/;
    const result = matcher.exec(dateStr)
    if (!result)
        return new Date('Invalid');

    const day = parseInt(result[1], 10);
    const year = parseInt(result[3], 10);
    const monthStr = result[2];
    let month = parseInt(monthStr, 10);
    if (!isNaN(month)) {
        month -= 1;
        if (month >= 12)
            return new Date('Invalid');
    }
    else {
        const parseResult = parseMonthName(monthStr)
        if (parseResult === undefined)
            return new Date('Invalid');

        month = parseResult;
    }
    
    const date = new Date(year, month, day);
    if (date.getDate() !== day || month !== date.getMonth() || year !== date.getFullYear())
        return new Date('Invalid');
    return date;
}

export interface DateFieldProps {
    value: string,
    hasError?: boolean,
    fromMonth?: Date,
    toMonth?: Date,
    initialMonth?: Date,
    disabledDays?: Matcher,
    dateCalendarModifiers?: Partial<Modifiers>,
    includeTodayButton?: boolean, 
    isRequired?: boolean,
    onChange: (newDate:string) => void
}

interface DateFieldState {
    dateInputText: string
    month: Date,
}

function updateDateFieldText(dateField:DateField, dateStr:string) {
    dateField.setState({dateInputText:dateStr});
    if (dateStr === '') {
        dateField.props.onChange('');
        return;
    }

    const parsed = parsePretty(dateStr);
    if (!isValid(parsed))
        return;

    dateField.props.onChange(format(parsed, 'yyyy-MM-dd'));
}

function resetInputField(field: DateField) {
    const text = field.props.value 
        ? format(parseISO(field.props.value), PrettyDateFormat)
        : '';

    field.setState({dateInputText: text})
}

function YearMonthForm(props:any) {
  const years = [];
  let fromMonth = props.fromMonth || new Date();
  let toMonth = props.toMonth || new Date(2040, 1, 1);

  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i);
  }

  const handleChange = function handleChange(e:any) {
    const { year, month } = e.target.form;
    props.onChange(new Date(year.value, month.value));
  };

  return (
    <form className="DayPicker-Caption">
      <select name="month" onChange={handleChange} value={props.date.getMonth()}>
        {monthNames.map((month:string, i:number) => <option key={i} value={i}>{month}</option>)}
      </select>
      <select name="year" onChange={handleChange} value={props.date.getFullYear()}>
        {years.map((year, i) =>
          <option key={i} value={year}>
            {year}
          </option>
        )}
      </select>
    </form>
  );
}

function dateFieldInput(field:DateField) {
    const isRequired = field.props.isRequired === true; 
    let asteriskIcon = undefined; 
    if (isRequired) {
        asteriskIcon = 
            <div className="ui left corner label">
                <i className="asterisk icon"></i>
            </div>
    }

    return <div className={"ui" + (isRequired ? " left corner labeled" : "") + " right icon input" + (field.props.hasError ? " error" : "")}>
         <i className="icon calendar" />
        <input 
            type="text" 
            value={field.state.dateInputText}
            onBlur={() => resetInputField(field)}
            onChange={(e) => updateDateFieldText(field, e.currentTarget.value)}
                  />
            {asteriskIcon}
    </div>;
}


function dateFieldPicker(field:DateField) {
    const selectedDays : Date[] = [ ];
    if (field.props.value)
        selectedDays.push(DateHelper.ToDate(field.props.value))

    function handleMonthChange(month:Date) {
        field.setState({month: month});
    }

    return <DayPicker
        mode="single"
        showOutsideDays
        fixedWeeks
        defaultMonth={field.props.initialMonth}
        selected={selectedDays[0]}
        weekStartsOn={1}
        disabled={field.props.disabledDays as Matcher | undefined}
        startMonth={field.props.fromMonth || new Date(2017, 0, 1)}
        endMonth={field.props.toMonth || new Date(2040, 1, 1)}
        month={field.state.month}
        onMonthChange={handleMonthChange}
        captionLayout="dropdown"
        modifiers={field.props.dateCalendarModifiers}
        footer={field.props.includeTodayButton === true ? "Go to Today" : undefined}
        onSelect={(day) => {
            if (!day) return;
            const dateFormat = format(day, 'yyyy-MM-dd');
            field.setState({dateInputText: format(day, PrettyDateFormat)});
            field.props.onChange(dateFormat);
        }}
        onDayClick={(day, modifiers) => {
            if (modifiers.disabled) return;
            const dateFormat = format(day, 'yyyy-MM-dd');
            field.setState({dateInputText: format(day, PrettyDateFormat)});
            field.props.onChange(dateFormat);
        }} />
}

function stateFromDateProp(dateStr:string, defaultMonth?: Date) {
    const date = dateStr ? DateHelper.ToDate(dateStr) : undefined;
    let formattedDate = date ? format(date, PrettyDateFormat) : '';
    const month = date ? date : (defaultMonth || new Date());
    return { dateInputText: formattedDate, month: month };
}

export default class DateField extends React.Component<DateFieldProps, DateFieldState> {
    constructor(props:DateFieldProps) {
        super(props);
        this.state = stateFromDateProp(props.value, props.initialMonth);
    }

    componentWillReceiveProps(nextProps:DateFieldProps) {
        
        //const prevDate = this.props.value;
        const newDate = nextProps.value;
        //const inputDate = format(parsePretty(this.state.dateInputText), 'yyyy-MM-dd');

        this.setState(stateFromDateProp(newDate, nextProps.initialMonth));        
    }

    render() {
        return <Popover 
            content={dateFieldPicker(this)}
            trigger="focus"
            placement="bottomLeft"
            styles={{body: {padding:0, borderRadius: 0, border: 'none'}}}>
            <div>{dateFieldInput(this)}</div>
        </Popover>
    }
}